import { Link, MessageBar, MessageBarType } from 'office-ui-fabric-react';
import { SectionToggle } from './SectionToggle';
import { ProgressCalculationType, TaskType } from '../../../entities/common';
import { ProjectCalculationSection } from './ProjectCalculationSection';
import { NewPpmxTasksCalculationModeSection } from './NewPpmxTasksCalculationModeSection';
import { SettingDropdown, SettingDropdownOption } from './SettingDropdown';
import './ScheduleCalculationSection.css'

type Props = {
    readonly?: boolean,
    value: boolean;
    onChange: (value: boolean) => void;
    progressCalculationType: ProgressCalculationType;
    onChangeProgressCalculationType: (value: ProgressCalculationType) => void;
    progressCalculationLocked?: boolean;
    isTaskAutoCalculationMode: boolean;
    onChangeMode: (value: boolean) => void;
    taskType: TaskType;
    onChangeTaskType: (value: TaskType) => void;
    effortDriven: boolean;
    onChangeEffortDriven: (value: boolean) => void;
};

export const taskTypeDescriptionsMap = {
    [TaskType.Flexible]: <>The task Duration, Effort, and Assigned To fields do not depend on each other and are entered and calculated separately.</>,
    [TaskType.FixedUnits]: <>Assignment units remain fixed. Effort is calculated based on Duration and assigned resources, and Duration adjusts when Effort changes.</>
};

const taskTypeOptions: SettingDropdownOption[] = [
    { key: TaskType.Flexible, text: 'Flexible', description: taskTypeDescriptionsMap[TaskType.Flexible] },
    { key: TaskType.FixedUnits, text: 'Fixed Units', description: taskTypeDescriptionsMap[TaskType.FixedUnits] }
];

const ScheduleCalculationSection = (props: Props) => {
    const {
        readonly, value, onChange,
        progressCalculationType, onChangeProgressCalculationType, progressCalculationLocked,
        isTaskAutoCalculationMode, onChangeMode,
        taskType, onChangeTaskType,
        effortDriven, onChangeEffortDriven
    } = props;

    const isEffort = progressCalculationType === ProgressCalculationType.Effort;
    const effectiveTaskType = isEffort ? taskType : TaskType.Flexible;
    const isFixedUnits = effectiveTaskType === TaskType.FixedUnits;
    const effectiveEffortDriven = isFixedUnits ? effortDriven : false;

    return (
        <div className="schedule-calculation">
            <div className='section-description'>
                Select how schedule and progress should be calculated for tasks in PPM Express. Please refer to <Link href="https://help.ppm.express/94162-portfolio-and-project-management/2082039-project-settings" target="_blank">this article</Link> for more details.
            </div>
            <ul className="cb-list">
                <SectionToggle
                    key="consider-calendars"
                    label="Enable calendar-based schedule calculation"
                    description={<div>
                        When turned ON, both project and resource calendars are used to determine working days during schedule calculation.
                        <br />
                        If calendars are updated, the project schedule is recalculated automatically.
                        <br />
                        When turned OFF (legacy state), resource calendars are ignored. Only the project calendar is used to determine working days during schedule calculation.
                        <br />
                        The project schedule is not recalculated automatically.
                        <br />
                        Please refer to the <Link href="https://help.ppm.express/projects/2082039-project-settings#section-10" target="_blank">article</Link> for more details.
                    </div>}
                    disabled={readonly}
                    checked={value}
                    onChange={onChange}
                />
            </ul>

            <ProjectCalculationSection
                value={progressCalculationType}
                readonly={readonly || progressCalculationLocked}
                lockedByFixedUnits={progressCalculationLocked}
                onChange={onChangeProgressCalculationType} />

            <NewPpmxTasksCalculationModeSection
                value={isTaskAutoCalculationMode}
                readonly={readonly}
                onChange={onChangeMode} />

            {!isEffort && <MessageBar messageBarType={MessageBarType.warning} isMultiline styles={{ root: { marginTop: 8 } }}>
                To change Task Type, set Calculate Progress by setting to Effort first.
            </MessageBar>}
            <SettingDropdown
                label="Task Type"
                required
                info={<>Select the default type for new tasks in PPM Express schedule. Please refer to <Link href="https://help.ppm.express/94162-portfolio-and-project-management/2082039-project-settings" target="_blank">this article</Link> for more details.</>}
                selectedKey={effectiveTaskType}
                options={taskTypeOptions}
                disabled={readonly || !isEffort}
                onChange={key => onChangeTaskType(key as TaskType)} />
            <MessageBar messageBarType={MessageBarType.warning} isMultiline styles={{ root: { marginTop: 8, marginBottom: 8 } }}>
                <b>Coming soon:</b> Additional task types will be introduced in future releases.
            </MessageBar>
            <ul className="cb-list">
                <SectionToggle
                    key="effort-driven"
                    label="Effort Driven"
                    description={<>Effort Driven determines whether the total Effort for a task remains constant when resources are added or removed.</>}
                    disabled={readonly || !isFixedUnits}
                    checked={effectiveEffortDriven}
                    onChange={onChangeEffortDriven}
                />
            </ul>
        </div>);
}
export default ScheduleCalculationSection;
